/**
 * Cart Bundles - Handles bundle quantity synchronization and removal
 */

class CartBundles {
  constructor() {
    this.init();
  }

  init() {
    this.bindBundleQuantityControls();
    this.bindBundleRemoveButtons();
  }

  /**
   * Bind quantity increase/decrease buttons for bundles
   */
  bindBundleQuantityControls() {
    document.querySelectorAll('[data-bundle-action]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const bundleId = button.dataset.bundleId;
        const action = button.dataset.bundleAction;

        if (action === 'increase') {
          this.changeBundleQuantity(bundleId, 1);
        } else if (action === 'decrease') {
          this.changeBundleQuantity(bundleId, -1);
        }
      });
    });
  }

  /**
   * Bind remove buttons for bundles
   */
  bindBundleRemoveButtons() {
    document.querySelectorAll('[data-bundle-remove]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const bundleId = button.dataset.bundleId;
        this.removeBundle(bundleId);
      });
    });
  }

  /**
   * Change quantity for all items in a bundle
   * @param {string} bundleId - The bundle ID
   * @param {number} delta - The change in quantity (+1 or -1)
   */
  async changeBundleQuantity(bundleId, delta) {
    const bundleRow = document.querySelector(`[data-bundle-id="${bundleId}"]`);
    if (!bundleRow) return;

    const quantityInput = bundleRow.querySelector(`#BundleQuantity-${bundleId}`);
    if (!quantityInput) return;

    const currentQuantity = parseInt(quantityInput.value, 10);
    const newQuantity = Math.max(0, currentQuantity + delta);

    if (newQuantity === 0) {
      // If quantity becomes 0, remove the bundle
      this.removeBundle(bundleId);
      return;
    }

    // Find all hidden component inputs for this bundle
    const componentInputs = document.querySelectorAll(
      `.cart-item--bundle-component[data-bundle-id="${bundleId}"] input[name="updates[]"]`
    );

    if (componentInputs.length === 0) {
      console.warn(`No components found for bundle ${bundleId}`);
      return;
    }

    // Update quantity for all components
    const updates = {};
    componentInputs.forEach(input => {
      const key = input.dataset.lineKey;
      if (key) {
        updates[key] = newQuantity;
      }
    });

    try {
      // Update the bundle quantity display immediately
      quantityInput.value = newQuantity;
      bundleRow.classList.add('cart-bundle--loading');

      // Submit the cart update
      await this.updateCart(updates);

      // Reload the cart to reflect changes
      this.reloadCart();
    } catch (error) {
      console.error('Error updating bundle quantity:', error);
      // Revert the display
      quantityInput.value = currentQuantity;
      bundleRow.classList.remove('cart-bundle--loading');
    }
  }

  /**
   * Remove all items in a bundle
   * @param {string} bundleId - The bundle ID to remove
   */
  async removeBundle(bundleId) {
    if (!confirm('Remove this bundle from your cart?')) {
      return;
    }

    const bundleRow = document.querySelector(`[data-bundle-id="${bundleId}"]`);
    if (!bundleRow) return;

    const componentInputs = document.querySelectorAll(
      `.cart-item--bundle-component[data-bundle-id="${bundleId}"] input[name="updates[]"]`
    );

    if (componentInputs.length === 0) {
      console.warn(`No components found for bundle ${bundleId}`);
      return;
    }

    const updates = {};
    componentInputs.forEach(input => {
      const key = input.dataset.lineKey;
      if (key) {
        updates[key] = 0; // Set quantity to 0 to remove
      }
    });

    try {
      bundleRow.classList.add('cart-bundle--loading');

      await this.updateCart(updates);

      // Reload the cart
      this.reloadCart();
    } catch (error) {
      console.error('Error removing bundle:', error);
      bundleRow.classList.remove('cart-bundle--loading');
    }
  }

  /**
   * Update cart via Shopify Cart API
   * @param {object} updates - Object with line item indices and quantities
   * @returns {Promise}
   */
  async updateCart(updates) {
    const response = await fetch(window.routes.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(`Cart update failed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Reload the cart section
   */
  reloadCart() {
    const cartForm = document.querySelector('#cart');
    if (cartForm) {
      // Trigger the existing cart update mechanism
      const event = new CustomEvent('cart:updated');
      document.dispatchEvent(event);

      // If there's a cart drawer or notification, trigger its refresh
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        fetch(window.routes.cart_url + '.js')
          .then(response => response.json())
          .then(cart => cartDrawer.renderContents(cart));
      } else if (cartNotification && typeof cartNotification.renderContents === 'function') {
        fetch(window.routes.cart_url + '.js')
          .then(response => response.json())
          .then(cart => cartNotification.renderContents(cart));
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
    } else {
      // No cart form, just reload
      window.location.reload();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartBundles();
  });
} else {
  new CartBundles();
}

// Reinitialize after cart updates
document.addEventListener('cart:updated', () => {
  setTimeout(() => {
    new CartBundles();
  }, 100);
});
